import React from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';

import { routes } from '@routes';

import { link } from '@utils/link';
import { hotlineEmail } from '@utils/contact';
import { messageIdConcat } from '@utils/message-id-concat';

import { Col, Row, Link, Icon, Text, H6, H4, H2 } from '@atoms';
import { Section } from '@molecules/section';

import { CompanyMobile, CompanyDesktop } from './footer-company';
import { FooterLink } from './footer-link';

import classes from './footer.module.scss';

export type FooterProps = {
  isHidden?: boolean;
  border?: boolean;
};

const m = messageIdConcat('footer');

const Support = () => {
  return (
    <div className={classes.support}>
      <div className={classes.wrap}>
        <H2 message={{ id: m('support.title') }} margin={0} />
        <H6
          message={{ id: m('support.subtitle') }}
          margin={[0, 0, 10]}
          color="slate_gray"
        />
        <Row className={classes.linkRow}>
          <Col className={classes.linkCol}>
            <Link
              href={`mailto:${hotlineEmail}`}
              className={classes.link}
              external
            >
              <>
                <Icon icon="mail" className={classes.icon} />
                <H6
                  message={{ text: hotlineEmail }}
                  color="azure_radiance"
                  margin={0}
                />
              </>
            </Link>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export const Footer = ({ isHidden, border }: FooterProps) => {
  if (isHidden) return null;

  return (
    <Section
      className={clsx(classes.footer, border && classes.footerBorder)}
      fluid
    >
      <div className={classes.container}>
        <Row className={classes.row}>
          {/* SUPPORT */}
          <Col
            xs={24}
            md={24}
            lg={{ span: 5, order: 1 }}
            xl={{ span: 5, order: 1 }}
          >
            <Support />
          </Col>

          {/* COMPANY */}
          <Col
            xs={{ span: 24, order: 2 }}
            md={{ span: 3, order: 0 }}
            lg={{ span: 3, order: 0 }}
            xl={4}
            className={classes.companyCol}
          >
            <CompanyMobile />
            <CompanyDesktop />
          </Col>

          {/* LINKS FIRST */}
          <Col xs={24} md={1} lg={1} xl={2} className={classes.linksCol}>
            <H4 message={{ id: 'meta.title' }} />
            <FooterLink
              {...link(routes.page, { path: 'o-nas' })}
              message={{ id: m('links.about') }}
              secondary
            />
            <FooterLink
              {...link(routes.blogHomepage)}
              message={{ id: m('links.blog') }}
              secondary
            />
            <FooterLink
              {...link(routes.page, { path: 'recenze' })}
              message={{ id: m('links.reviews') }}
              secondary
            />
            <FooterLink
              {...link(routes.page, { path: 'kontakt' })}
              message={{ id: m('links.contact') }}
              secondary
            />
          </Col>

          {/* LINKS SECOND */}
          <Col xs={24} md={3} lg={3} xl={4} className={classes.linksCol}>
            <H4 message={{ id: m('links.estate_sale') }} />
            <FooterLink
              {...link(routes.page, { path: 'okamzita-platba' })}
              message={{ id: m('links.instant_offer') }}
              secondary
            />
            <FooterLink
              {...link(routes.page, { path: 'cenova-mapa' })}
              message={{ id: m('links.atlas') }}
              secondary
            />
            <FooterLink
              {...link(routes.page, { path: 'odhad-ceny' })}
              message={{ id: m('links.estimate_price') }}
              secondary
            />
          </Col>

          {/* LINKS THIRD */}
          <Col xs={24} md={4} lg={2} xl={3} className={classes.linksCol}>
            <H4 message={{ id: m('links.co_operation') }} />
            <FooterLink
              {...link(routes.page, { path: 'partner' })}
              message={{ id: m('links.for_estate_brokers') }}
              secondary
            />
            <FooterLink
              {...link(routes.page, { path: 'pro-kupujici' })}
              message={{ id: m('links.for_buyers') }}
              secondary
            />
            <FooterLink
              href="/page?path=kontakt"
              as="/kontakt#kontakty"
              message={{ id: m('links.for_press') }}
              secondary
            />
            <FooterLink
              href="https://www.startupjobs.cz/startup/reas-cz"
              message={{ id: m('links.jobs') }}
              external
              secondary
            />
          </Col>

          {/* LEGALS */}
          <Col xs={{ span: 24, order: 4 }}>
            <Row className={classes.legalsRow}>
              <Col className={classes.legalsLinks}>
                <FooterLink
                  {...link(routes.page, { path: 'obchodni-podminky' })}
                  message={{ id: m('links.terms_and_conditions') }}
                  secondary
                />
                |
                <FooterLink
                  {...link(routes.page, { path: 'ochrana-osobnich-udaju' })}
                  message={{ id: m('links.privacy') }}
                  secondary
                />
                |
                <FooterLink
                  {...link(routes.page, { path: 'vyuziti-souboru-cookies' })}
                  message={{ id: m('links.cookies') }}
                  secondary
                />
              </Col>
              <Col>
                <Text
                  message={{
                    id: 'footer.copyright',
                    values: { year: dayjs().year() },
                  }}
                  color="heather"
                  type="small"
                  fontWeight={700}
                  margin={[10, 0, 10]}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </Section>
  );
};
